import React from 'react';
import { Card } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register the required chart components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TaskTrend = ({ trendData, title = 'Task Trend' }) => {
  if (!trendData || trendData.length === 0) {
    return (
      <Card className="h-100">
        <Card.Header>{title}</Card.Header>
        <Card.Body className="d-flex align-items-center justify-content-center">
          <p className="text-muted">No trend data available</p>
        </Card.Body>
      </Card>
    );
  }

  // Sort data by date
  const sortedData = [...trendData].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Format dates for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const labels = sortedData.map(item => formatDate(item.date));
  
  // Extract data for each series
  const completedTasks = sortedData.map(item => item.completed);
  const pendingTasks = sortedData.map(item => item.pending);
  const totalTasks = sortedData.map(item => (item.completed + item.pending));

  const data = {
    labels,
    datasets: [
      {
        label: 'Total Tasks',
        data: totalTasks,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: 'rgba(75, 192, 192, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        pointRadius: 3,
      },
      {
        label: 'Completed',
        data: completedTasks,
        borderColor: 'rgba(40, 167, 69, 1)',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        tension: 0.4,
        fill: false,
        pointBackgroundColor: 'rgba(40, 167, 69, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        pointRadius: 3,
      },
      {
        label: 'Pending',
        data: pendingTasks,
        borderColor: 'rgba(255, 193, 7, 1)',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
        tension: 0.4,
        fill: false,
        pointBackgroundColor: 'rgba(255, 193, 7, 1)',
        pointBorderColor: '#fff',
        pointBorderWidth: 1,
        pointRadius: 3,
      }
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.raw;
            return `${label}: ${value} tasks`;
          }
        }
      }
    },
  };

  return (
    <Card className="h-100">
      <Card.Header>{title}</Card.Header>
      <Card.Body>
        <div className="chart-container" style={{ height: '300px' }}>
          <Line data={data} options={options} />
        </div>
      </Card.Body>
    </Card>
  );
};

TaskTrend.propTypes = {
  trendData: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      completed: PropTypes.number.isRequired,
      pending: PropTypes.number.isRequired
    })
  ),
  title: PropTypes.string
};

export default TaskTrend; 